import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    inject,
    input,
    Signal,
    signal,
} from '@angular/core';
import {
    TelXLActivityResultTagComponent,
    TelXLMediaTagComponent,
    TelXLPanelComponent,
    TelXLTagComponent,
} from '@dxp/elements';
import {
    channelTypeToQueueMediaType,
    secondsToDuration,
} from '@dxp/core/helpers';
import { IHistoricActivity, IMessageHistory } from '@dxp/core/models';
import { FormatDurationPipe } from '@dxp/core/pipes';
import { USER_STORE } from '@dxp/core/stores';
import { TelXLActivityHistoryConversationComponent } from './conversation/activity-history-conversation.component';

@Component({
    selector: 'telxl-shared-activity-history-item',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        TelXLPanelComponent,
        TelXLTagComponent,
        TelXLMediaTagComponent,
        TelXLActivityResultTagComponent,
        TelXLActivityHistoryConversationComponent,
    ],
    providers: [FormatDurationPipe],
    templateUrl: './activity-history-item.component.html',
    styleUrl: './activity-history-item.component.scss',
})
export class TelXLActivityHistoryItemComponent {
    readonly #userStore = inject(USER_STORE);
    readonly #formatDurationPipe = inject(FormatDurationPipe);

    channelTypeToQueueMediaType = channelTypeToQueueMediaType;

    variation = input.required<'agent' | 'customer'>();
    activity = input.required<IHistoricActivity>();
    isExpanded = signal<boolean>(false);

    chatActivity: Signal<IMessageHistory[] | null> = signal(null);

    toggleExpandedView() {
        this.isExpanded.update(current => !current);
    }

    get duration(): string {
        const duration = this.#formatDurationPipe.transform(
            secondsToDuration(this.activity().duration),
            '[dd]:hh:mm:ss',
        );

        return `Duration: ${duration}`;
    }

    get completioncode(): string {
        return `Completion Code: ${this.activity().tag.length > 0 ? this.activity().tag : 'None'}`;
    }

    get agentName(): string {
        if (
            this.#userStore.hideSensitiveAgentInformation() &&
            this.activity().agent?.id !== this.#userStore.user().id
        ) {
            return 'Other Agent';
        }

        const agent = this.activity().agent?.name ?? '';
        return agent.length > 0 ? agent : 'Unknown Agent';
    }

    get customerName(): string {
        const customer = this.activity().customerName ?? '';
        return customer.length > 0 ? customer : 'Unknown Customer';
    }
}
